import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { calculateProgressivePAYE } from '../../utils/payeCalculations'
import { TAX_YEARS } from '../../constants/taxConstants'

const LATEST_TAX_YEAR = (() => {
  const yearKeys = Object.keys(TAX_YEARS)
  return yearKeys.sort((a, b) => parseInt(b.split('/')[0], 10) - parseInt(a.split('/')[0], 10))[0]
})()

const DEFAULT_TAX_YEAR = LATEST_TAX_YEAR

// Privacy: ensure previous localStorage data is cleared if it exists
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('wizard-store-v1')
  }
} catch (_) {
  // no-op
}

const getInitialInputs = (selectedTaxYear) => {
  const is2025OrLater = selectedTaxYear === '2025/2026' || parseInt(selectedTaxYear.split('/')[0]) >= 2025
  return {
    isTaxResidentInScotland: false,
    baseSalary: '',
    bonus: '',
    medicalBenefit: '',
    voldeductions: '',
    rsus: '',
    capitalGains: '',
    rsuemployerNICrate: 6.9,
    employerMaxPensionContributionPct: 5,
    rsuTaxRate: 50,
    rsuVestEvents: [],
    studentLoanPlans: [],
    isEmployerNicLiable: !is2025OrLater,
    employerNicManuallyChanged: false,
    employeePensionContribution: 0,
    employeePensionContributionMode: 'percentage',
    employeePensionContributionPeriod: 'annual',
    // New: Additional Benefits & Salary Sacrifice (annualised values)
    childcareVouchers: 0,
    cycleToWork: 0,
    electricCar: 0,
    companyCarBenefit: 0,
  }
}

export const useWizardStore = create(persist((set, get) => ({
  selectedTaxYear: DEFAULT_TAX_YEAR,
  inputs: getInitialInputs(DEFAULT_TAX_YEAR),
  rsuEnabled: false,

  setSelectedTaxYear: (year) => {
    set((state) => ({
      selectedTaxYear: year,
      // Respect manual NIC toggle if set; otherwise follow App.js behavior
      inputs: state.inputs.employerNicManuallyChanged
        ? state.inputs
        : { ...state.inputs, isEmployerNicLiable: !(year === '2025/2026' || parseInt(year.split('/')[0]) >= 2025) },
    }))
  },

  setInput: (name, value) => {
    set((state) => ({ inputs: { ...state.inputs, [name]: value } }))
  },

  setInputs: (partial) => set((state) => ({ inputs: { ...state.inputs, ...partial } })),

  resetInputs: () => set((state) => ({ inputs: getInitialInputs(state.selectedTaxYear) })),

  // Reset everything to app defaults, including tax year and RSU toggle
  resetAll: () => set(() => ({
    selectedTaxYear: DEFAULT_TAX_YEAR,
    inputs: getInitialInputs(DEFAULT_TAX_YEAR),
    rsuEnabled: false,
  })),

  setEmployerNicLiable: (isLiable) => set((state) => ({
    inputs: { ...state.inputs, isEmployerNicLiable: isLiable, employerNicManuallyChanged: true },
  })),

  setRsuEnabled: (enabled) => set({ rsuEnabled: enabled }),

  // Dedicated handler to mirror App.js RSU vest processing logic
  setRsuVestEvents: (events) => set((state) => {
    const processed = events && events.length > 0
      ? calculateProgressivePAYE(
          events,
          { baseSalary: state.inputs.baseSalary, bonus: state.inputs.bonus },
          state.inputs.isTaxResidentInScotland,
          state.selectedTaxYear,
        )
      : []

    if (!events || events.length === 0) {
      return {
        inputs: {
          ...state.inputs,
          rsuVestEvents: [],
          rsus: '',
          capitalGains: '',
          rsuemployerNICrate: 6.9,
        },
        rsuEnabled: false,
      }
    }

    const totalRsuAmount = events.reduce((sum, vest) => sum + (Number(vest.amount) || 0), 0)
    const totalCapitalGains = events.reduce(
      (sum, vest) => sum + (vest.sold_immediately ? 0 : (Number(vest.capital_gains) || 0)),
      0,
    )
    const totalNICAmount = events.reduce(
      (sum, vest) => sum + ((Number(vest.amount) || 0) * ((Number(vest.employer_nic_rate) || 6.9) / 100)),
      0,
    )
    const avgNICRate = totalRsuAmount > 0 ? (totalNICAmount / totalRsuAmount) * 100 : 6.9

    return {
      inputs: {
        ...state.inputs,
        rsuVestEvents: processed,
        rsus: totalRsuAmount,
        capitalGains: totalCapitalGains,
        rsuemployerNICrate: parseFloat(avgNICRate.toFixed(1)),
      },
      rsuEnabled: true,
    }
  }),
}), {
  name: 'wizard-store-v1',
  storage: createJSONStorage(() => (
    typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage
      : {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        }
  )),
}))